import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import map from 'lodash/map'
import filter from 'lodash/filter'
import ProjectVersionLanguageTts from './ProjectVersionLanguageTts'
import TranslationLanguagesTts from './TranslationLanguagesTts'
import SetupWarningPopup, { type IWarning } from '@SiteComponents/SetupWarningPopup/SetupWarningPopup'
import ImportScript from './ImportScript'
import ParametersTts from './ParametersTts'
import Button from '@SiteComponents/Button'
import Popup from '@SiteComponents/Popup'
import Cancel from '@Images/new_project_popup_cancel.svg'
import Continue from '@Images/new_project_popup_continue.svg'

import {
  ISetupTtsPayload,
} from '@SiteModules/ProjectVersionSettingsTts/types'

import {
  fetchProjectVersionSettingsTts,
  resetProjectVersionSettingsTts,
  setupTts,
  fetchTranslationLanguagesTts,
  resetTranslationLanguagesTts,
} from '@SiteModules/ProjectVersionSettingsTts/actions'

import {
  fetchTermbases,
} from '@SiteModules/Termbase/actions'

import {
  closeSetupProjectPopupTts,
} from '@SiteModules/Popup/actions'

import {
  getHasLoadedUserDataSuccessfully
} from '@SiteModules/User/selectors'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getHasLoadedProjectVersionSettingsTtsSuccessfully,
  getLastUsedSubtitleLanguages,
  getLastUsedTranscriptLanguage,
  getLastUsedFormality,
  getLastUsedTermbaseName,
  getAvailableDubbingLanguages,
  getAvailableDubbingLanguagesTranslation,
  getExistingProjectVersionLanguage,
  getExistingTranslationLanguages,
  getFetchingTranslationLanguages,
} from '@SiteModules/ProjectVersionSettingsTts/selectors'

import {
  getTermbasesHasLoadedInitially,
  getTermbasesLoading,
  getTermbases,
} from '@SiteModules/Termbase/selectors'

import {
  getNewProjectPopupIsOpen,
  getEditingProjectVersionIds,
} from '@SiteModules/Popup/selectors'

import {
  getCurrentAccountSubscriptionMaxLanguagesLimit,
  getUserIsAccountAdmin,
  getCurrentAccountAdminEmails,
  getCurrentAccountTranslationProvider,
} from '@SiteModules/Account/selectors'

import './SetupProjectPopupTts.scss'

const SetupProjectPopupTts: React.FC = () => {
  const dispatch = useDispatch()

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const newProjectPopupIsOpen = useSelector(getNewProjectPopupIsOpen)

  const availableDubbingLanguages = useSelector(getAvailableDubbingLanguages)
  const availableDubbingLanguagesTranslation = useSelector(getAvailableDubbingLanguagesTranslation)
  const termbasesHasLoadedInitially = useSelector(getTermbasesHasLoadedInitially)
  const termbasesLoading = useSelector(getTermbasesLoading)

  const hasLoadedUserDataSuccessFully = useSelector(getHasLoadedUserDataSuccessfully)
  const hasLoadedProjectVersionSettingsTtsSuccessfully = useSelector(getHasLoadedProjectVersionSettingsTtsSuccessfully)
  const loading = termbasesLoading
  const hasLoadedSuccessfully = !loading && hasLoadedUserDataSuccessFully && hasLoadedProjectVersionSettingsTtsSuccessfully && termbasesHasLoadedInitially

  const editingProjectVersionIds = useSelector(getEditingProjectVersionIds)
  const termbases = useSelector(getTermbases)
  const lastUsedTranscriptLanguage = useSelector(getLastUsedTranscriptLanguage)
  const lastUsedSubtitleLanguages = useSelector(getLastUsedSubtitleLanguages)
  const lastUsedFormality = useSelector(getLastUsedFormality)
  const lastUsedTermbaseName = useSelector(getLastUsedTermbaseName)
  const currentAccountSubscriptionMaxLanguagesLimit = useSelector(getCurrentAccountSubscriptionMaxLanguagesLimit) || 0
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const currentAccountAdminEmails = useSelector(getCurrentAccountAdminEmails)
  const translationProvider = useSelector(getCurrentAccountTranslationProvider)
  const fetchingTranslationLanguages = useSelector(getFetchingTranslationLanguages)

  const [newProjectVersionLanguage, setNewProjectVersionLanguage] = useState('')
  const [termbaseOption, setTermbaseOption] = useState<string | null>(null)
  const [translationToneOption, setTranslationToneOption] = useState<string | null>('Auto tone')
  const [showProjectVersionLanguageAlert, setShowProjectVersionLanguageAlert] = useState(false)
  const [showTranslationLanguagesAlert, setShowTranslationLanguagesAlert] = useState(false)
  const [showMissingScriptAlert, setShowMissingScriptAlert] = useState(false)
  const [shouldImportScript, setShouldImportScript] = useState(false)
  const [scriptText, setScriptText] = useState('')
  const [scriptFileName, setScriptFileName] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [warningsToShow, setWarningsToShow] = useState<IWarning[]>([])
  const [ignoredWarnings, setIgnoredWarnings]= useState<string[]>([])
  const [shouldTranslate, setShouldTranslate] = useState(false)
  const [newTranslationLanguages, setNewTranslationLanguages] = useState<string[]>([])

  const isNonExistingProject = editingProjectVersionIds.length === 0
  const existingProjectVersionLanguage = useSelector(getExistingProjectVersionLanguage)
  const existingTranslationLanguages = useSelector(getExistingTranslationLanguages)
  const hasNewTranscription = existingProjectVersionLanguage.length === 0
  const isNewProject = hasNewTranscription
  const isNewProjectWithTranslation = isNewProject && shouldTranslate
  const isExistingProject = !isNewProject
  const existingSubtitlesLength = isNewProject ? 0 : existingTranslationLanguages.length + 1
  const projectVersionLanguage = hasNewTranscription ? newProjectVersionLanguage : existingProjectVersionLanguage
  const willTranslate = isNewProjectWithTranslation || isExistingProject
  const willUseTranslationTone = willTranslate && translationToneOption !== 'Auto tone'// In the backend, the translation tone is only applied if the language has a variable translation tone

  const newSubtitlesLength = hasNewTranscription ? newTranslationLanguages.length + 1 : newTranslationLanguages.length
  const totalNumberOfSubscriptionSubtitles = existingSubtitlesLength + newSubtitlesLength
  const isLanguageLimitReached = currentAccountSubscriptionMaxLanguagesLimit < totalNumberOfSubscriptionSubtitles

  const projectVersionLanguageSelectionIncomplete = isNewProject && newProjectVersionLanguage.length === 0
  const translationLanguageSelectionIncomplete= willTranslate && newTranslationLanguages.length === 0
  const scriptUploadIncomplete = shouldImportScript && scriptText.length === 0
  const showWarning = warningsToShow.length > 0

  const shouldDisableCTAButton = showProjectVersionLanguageAlert ||
    showTranslationLanguagesAlert ||
    showMissingScriptAlert ||
    !hasLoadedSuccessfully ||
    isSubmitted

  useEffect(() => {
    dispatch(fetchProjectVersionSettingsTts(
      {
        workspaceId: currentWorkspaceId,
        ...(isNonExistingProject ? {} : { projectVersionIds: editingProjectVersionIds }),
      }
    ))

    if (!termbasesHasLoadedInitially && !termbasesLoading) {
      dispatch(fetchTermbases({ workspaceId: currentWorkspaceId }))
    }

    return () => {
      dispatch(closeSetupProjectPopupTts())
      dispatch(resetProjectVersionSettingsTts())
    }
  }, [])

  useEffect(() => {
    if (hasLoadedSuccessfully) {
      let initialProjectVersionLanguage = existingProjectVersionLanguage

      if (hasNewTranscription && lastUsedTranscriptLanguage) {
        initialProjectVersionLanguage = lastUsedTranscriptLanguage
        setNewProjectVersionLanguage(lastUsedTranscriptLanguage)
      }

      if (hasNewTranscription && existingTranslationLanguages.length === 0 && lastUsedSubtitleLanguages) {
        const initialTranslationLanguages = filter(lastUsedSubtitleLanguages, subtitleLanguage => subtitleLanguage !== initialProjectVersionLanguage)
        setNewTranslationLanguages(initialTranslationLanguages)

        if (isNewProject && initialTranslationLanguages.length > 0) {
          setShouldTranslate(true)
        }
      }

      const termbaseNames = map(termbases, termbase => termbase.attributes.name)
      if (lastUsedTermbaseName && termbaseNames.includes(lastUsedTermbaseName)) {
        setTermbaseOption(lastUsedTermbaseName)
      }

      if (lastUsedFormality && lastUsedFormality === 'more') {
        setTranslationToneOption('Formal tone')
      } else if (lastUsedFormality && lastUsedFormality === 'less') {
        setTranslationToneOption('Informal tone')
      }
    }
  }, [hasLoadedSuccessfully])

  useEffect(() => {
    if (!projectVersionLanguageSelectionIncomplete && showProjectVersionLanguageAlert) {
      setShowProjectVersionLanguageAlert(false)
    }
  }, [projectVersionLanguageSelectionIncomplete])

  useEffect(() => {
    if (!translationLanguageSelectionIncomplete && showTranslationLanguagesAlert) {
      setShowTranslationLanguagesAlert(false)
    }
  }, [translationLanguageSelectionIncomplete])

  useEffect(() => {
    if (!scriptUploadIncomplete && showMissingScriptAlert) {
      setShowMissingScriptAlert(false)
    }
  })

  useEffect(() => {
    if (ignoredWarnings.length > 0) {
      submit()
    }
  }, [ignoredWarnings])

  const handleFetchTranslationLanguages = () => {
    if (projectVersionLanguageSelectionIncomplete) {
      dispatch(resetTranslationLanguagesTts())
    } else {
      dispatch(fetchTranslationLanguagesTts({
        workspaceId: currentWorkspaceId,
        projectVersionLanguage,
        isForTts: true,
        ...(isNonExistingProject ? {} : { projectVersionIds: editingProjectVersionIds }),
      }))
    }
  }

  const handleShowTranslationLanguages = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newShouldTranslate = e.target.checked
    setShouldTranslate(newShouldTranslate)

    if (!newShouldTranslate) {
      setNewTranslationLanguages([])
    }
  }

  const handleClose = () => {
    dispatch(closeSetupProjectPopupTts())
  }

  const handleContinue = () => {
    if (projectVersionLanguageSelectionIncomplete || translationLanguageSelectionIncomplete || scriptUploadIncomplete) {
      if (projectVersionLanguageSelectionIncomplete) {
        setShowProjectVersionLanguageAlert(true)
      }

      if (translationLanguageSelectionIncomplete) {
        setShowTranslationLanguagesAlert(true)
      }

      if (scriptUploadIncomplete) {
        setShowMissingScriptAlert(true)
      }
    } else {
      submit()
    }
  }

  const resetWarnings = () => {
    setWarningsToShow([])
    setIgnoredWarnings([])
  }

  const updateIgnoredWarningsAndTrySubmit = (warningsToIgnore: string[]) => {
    setIgnoredWarnings(ignoredWarnings.concat(warningsToIgnore))
    // submit is called in useEffect
  }

  const submit = () => {
    const newWarningsToShow = getWarningsToShow()
    setWarningsToShow(newWarningsToShow)

    if (newWarningsToShow.length === 0) {
      let formality: string | null = null

      switch(translationToneOption) {
        case 'Auto tone':
          formality = 'default'
          break
        case 'Formal tone':
          formality = 'more'
          break
        case 'Informal tone':
          formality = 'less'
          break
      }

      const payload: ISetupTtsPayload = {
        projectVersionLanguage,
        workspaceId: currentWorkspaceId,
        includesTranscript: hasNewTranscription,
        ...(shouldImportScript ? { scriptText: scriptText.trim() } : {}),
        ...(willTranslate ? { translationLanguages: newTranslationLanguages } : {}),
        ...(termbaseOption ? { termbaseName: termbaseOption } : {}),
        ...((willUseTranslationTone && !!formality) ? { formality } : {}),
        ...(isNonExistingProject ? {} : { projectVersionId: editingProjectVersionIds[0] })
      }

      dispatch(setupTts(payload))
      setIsSubmitted(true)
    }
  }

  const getWarningsToShow = () => {
    const warnings: IWarning[] = []

    if (isLanguageLimitReached) {
      warnings.push({ type: 'too_many_languages_for_subscription' })
    }

    return filter(warnings, warning => !ignoredWarnings.includes(warning.type))
  }

  const renderProjectLanguage = () => {
    return (
      <div className="SetupProjectPopupTts__section">
        <div className="SetupProjectPopupTts__project-version-language">
          <div className="SetupProjectPopupTts__section-header">
            Language of your project
          </div>
          <ProjectVersionLanguageTts
            newProjectVersionLanguage={newProjectVersionLanguage}
            setNewProjectVersionLanguage={setNewProjectVersionLanguage}
            availableProjectVersionLanguages={availableDubbingLanguages}
            showAlert={showProjectVersionLanguageAlert}
            handleFetchTranslationLanguages={handleFetchTranslationLanguages}
          />
        </div>
      </div>
    )
  }

  const renderImportScript = () => {
    return (
      <div className="SetupProjectPopupTts__section">
        <ImportScript
          shouldImportScript={shouldImportScript}
          setShouldImportScript={setShouldImportScript}
          setScriptText={setScriptText}
          scriptText={scriptText}
          showAlert={showMissingScriptAlert}
          scriptFileName={scriptFileName}
          setScriptFileName={setScriptFileName}
        />
      </div>
    )
  }

  const renderTranslationSwitch = () => {
    return (
      <div className="SetupProjectPopupTts__switch">
        <label className="SetupProjectPopupTts__switch-label">
          <input
            type="checkbox"
            checked={shouldTranslate}
            onChange={handleShowTranslationLanguages}
          />
          <span className="SetupProjectPopupTts__switch-slider" />
        </label>
        Translate the voice-over
      </div>
    )
  }

  const renderTranslationLanguages = () => {
    return (
      <div className="SetupProjectPopupTts__section">
        {!isNewProject && (
          <div className="SetupProjectPopupTts__section-header">
            Add translation languages
          </div>
        )}
        <TranslationLanguagesTts
          isNewProject={isNewProject}
          newProjectVersionLanguage={newProjectVersionLanguage}
          existingProjectVersionLanguage={existingProjectVersionLanguage}
          newTranslationLanguages={newTranslationLanguages}
          existingTranslationLanguages={existingTranslationLanguages}
          availableTranslationLanguages={availableDubbingLanguagesTranslation}
          setNewTranslationLanguages={setNewTranslationLanguages}
          showAlert={showTranslationLanguagesAlert}
          translationProvider={translationProvider}
          handleFetchTranslationLanguages={handleFetchTranslationLanguages}
          fetchingTranslationLanguages={fetchingTranslationLanguages}
        />
      </div>
    )
  }

  const renderParameters = () => {
    return (
      <div className="SetupProjectPopupTts__section">
        <ParametersTts
          termbaseOption={termbaseOption}
          setTermbaseOption={setTermbaseOption}
          termbases={termbases}
          translationToneOption={translationToneOption}
          setTranslationToneOption={setTranslationToneOption}
          willTranslate={willTranslate}
        />
      </div>
    )
  }

  const renderMainForm = () => {
    return (
      <div className="SetupProjectPopupTts__main">
        {isNewProject && (
          <React.Fragment>
            {renderProjectLanguage()}
            {renderImportScript()}
            {renderTranslationSwitch()}
          </React.Fragment>
        )}
        {(shouldTranslate || !isNewProject) && renderTranslationLanguages()}
        {renderParameters()}
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <div className="SetupProjectPopupTts__bottom">
        <div className="SetupProjectPopupTts__bottom-left">
          <Button
            caption="Cancel"
            icon={<Cancel />}
            onClick={handleClose}
            styleType="gray-subtle"
            size="small"
          />
        </div>
        <div className="SetupProjectPopupTts__bottom-right">
          <Button
            caption="Continue"
            icon={<Continue />}
            onClick={handleContinue}
            styleType="brand"
            size="small"
            iconRight
            disabled={shouldDisableCTAButton}
          />
        </div>
      </div>
    )
  }

  const renderWarningPopup = () => {
    return (
      <SetupWarningPopup
        warningsToShow={warningsToShow}
        handleClose={handleClose}
        handleCloseAndKeepEverything={handleClose}
        submit={submit}
        resetWarnings={resetWarnings}
        updateIgnoredWarningsAndTrySubmit={updateIgnoredWarningsAndTrySubmit}
        hasNewTranscription={hasNewTranscription}
        projectVersionLanguage={projectVersionLanguage}
        userIsAccountAdmin={userIsAccountAdmin}
        adminEmails={currentAccountAdminEmails}
        currentAccountSubscriptionMaxLanguagesLimit={currentAccountSubscriptionMaxLanguagesLimit}
        totalNumberOfSubscriptionSubtitles={totalNumberOfSubscriptionSubtitles}
        isNonExistingProject={isNewProject}
        newTranslationLanguages={newTranslationLanguages}
        shouldNotRenderOverlay={newProjectPopupIsOpen}
      />
    )
  }

  const renderProjectSetupPopupTts = () => {
    return (
      <Popup
        className="SetupProjectPopupTts"
        zIndex={200000}
        shouldNotRenderOverlay={newProjectPopupIsOpen}
        closePopup={handleClose}
      >
        <div className="SetupProjectPopupTts__header">
          {isNewProject ? 'Create a voice-over' : 'Translate a voice-over'}
        </div>
        {renderMainForm()}
        {renderBottom()}
      </Popup>
    )
  }

  return (
    <React.Fragment>
      {hasLoadedSuccessfully && !showWarning && renderProjectSetupPopupTts()}
      {showWarning && renderWarningPopup()}
    </React.Fragment>
  )
}

export default SetupProjectPopupTts
